"use client";

import { useKioskEvent } from "@/app/directories/kiosk/contexts/KioskEventContext";
import { getTranslatedHrtName } from "@/app/directories/shop/helper";
import {
  type TypedTranslationFunction,
  useTranslations,
} from "@/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { cn } from "@frontend/shared/lib/utils";
import { motion } from "framer-motion";
import { useLocale } from "next-intl";
import type { HrtOptionsGridProps } from "./HrtOptionsGrid";

export function renderPrice(
  price: number,
  isLoading: boolean,
  isInsuredPending: boolean,
  t: TypedTranslationFunction<"Shop">,
): React.ReactNode {
  if (isLoading) {
    return <p className="text-sm font-mono">{t("Loading")}</p>;
  }
  if (isInsuredPending) {
    return (
      <p className="text-sm">
        {t("InsuredPriceIs")}{" "}
        <span className="text-superscriptBlue">{t("Pending")}</span>
      </p>
    );
  }
  return (
    <p className="text-sm font-mono">
      $
      {price.toLocaleString("en-US", {
        minimumFractionDigits: 2,
      })}
    </p>
  );
}

export function VerticalHrtOptionsGrid({
  options,
  selectedHrtId,
  onHrtSelect,
  pricingMap,
  calculatePrice,
  secondaryColor,
}: HrtOptionsGridProps) {
  const t = useTranslations("Shop");
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const { isPendingPricesMode: isKioskPendingPricesMode } = useKioskEvent();

  return (
    <div className="flex flex-col gap-4">
      {options.map((option) => {
        const priceEntry = pricingMap[option.hrt.hrtId];
        const price = calculatePrice(option, pricingMap);
        const isSelected = selectedHrtId === option.hrt.hrtId;

        // Explicit states
        const isLoading = priceEntry === undefined;
        const isInsuredPending =
          (isPendingPricesMode || isKioskPendingPricesMode) &&
          priceEntry?.mode === "INSURED";

        return (
          <motion.button
            key={option.hrt.hrtId}
            type="button"
            layout
            onClick={() => onHrtSelect(option.hrt.hrtId)}
            className={cn(
              "flex flex-row justify-between items-center lg:gap-1 sm:gap-0 border rounded-2xl px-5 text-left relative overflow-hidden",
              options.length === 2 &&
                (isSelected ? "col-span-3" : "col-span-2"),
            )}
            style={{
              paddingTop: isSelected ? 16 : 12,
              paddingBottom: isSelected ? 16 : 12,
              borderColor: isSelected ? `${secondaryColor}` : "#E5E7EB",
              borderWidth: isSelected ? 2 : 1,
              backgroundColor: isSelected
                ? "rgba(85, 195, 182, 0.05)"
                : "transparent",
            }}
            animate={{
              paddingTop: isSelected ? 16 : 12,
              paddingBottom: isSelected ? 16 : 12,
              borderColor: isSelected ? `${secondaryColor}` : "#E5E7EB",
              borderWidth: isSelected ? 2 : 1,
              backgroundColor: isSelected
                ? "rgba(85, 195, 182, 0.05)"
                : "transparent",
            }}
            whileHover={
              !isSelected
                ? {
                    borderColor: `${secondaryColor}`,
                    borderWidth: 1.5,
                  }
                : {}
            }
            transition={{
              paddingTop: { duration: 0.15, ease: [0.4, 0, 0.2, 1] },
              paddingBottom: { duration: 0.15, ease: [0.4, 0, 0.2, 1] },
              backgroundColor: { duration: 0.15, ease: [0.4, 0, 0.2, 1] },
              borderColor: { duration: 0 },
              borderWidth: { duration: 0 },
            }}
          >
            <p className="lg:font-bold sm:font-medium sm:text-sm lg:text-sm lg:leading-[18px] sm:leading-[18px]">
              {getTranslatedHrtName(option.hrt, languageCode)}
            </p>
            {renderPrice(price, isLoading, isInsuredPending, t)}
          </motion.button>
        );
      })}
    </div>
  );
}
